import React from "react";
import StatWrapper from "../shared/StatWrapper";
import PieChart from "../shared/PieChart";
import Heading from "hardcover-ui/components/Heading";
import Text from "hardcover-ui/components/Text";
import { useChartTheme } from "../contexts/ChartThemeContext";
import { usePage } from "@inertiajs/react";
import { UserStatsPageProps } from "pages/clientverse/users/stats/index.props";
import LocaleNumber from "components/ui/LocaleNumber";
import pluralize from "lib/pluralize";

interface GenreBreakdownStatProps {
  size?: "sm" | "md" | "lg" | "xl";
}

const GenreBreakdownStat: React.FC<GenreBreakdownStatProps> = ({ size }) => {
  const { stats } = usePage<UserStatsPageProps>().props;
  const { fictionVsNonfiction: data } = stats;

  if (!data.genres || data.genres.length === 0) {
    return (
      <StatWrapper title="Fiction vs. Non-Fiction" size={size}>
        <Heading size="sm" className="mb-4">
          No genre data available
        </Heading>
      </StatWrapper>
    );
  }

  const { getTheme, getMonotheme } = useChartTheme();

  const colors = [
    {
      color: getTheme(3),
      mono: getMonotheme(1),
    },
    {
      color: getTheme(2),
      mono: getMonotheme(5),
    },
    {
      color: getMonotheme(10),
      mono: getMonotheme(10),
    },
  ];

  const mainGenre = data.genres[0];

  const genresWithColors = data.genres.map((genre, index) => ({
    ...genre,
    color: colors[index].color,
  }));

  const totalBooks = data.genres.reduce(
    (sum, item) => sum + (item.value || 0),
    0
  );

  return (
    <StatWrapper title="Fiction vs. Non-Fiction" size={size}>
      <div className="flex flex-col h-full w-full">
        <Heading size="sm" className="mb-4">
          You've mostly read {mainGenre?.label || "no books"}.
        </Heading>
        <div className="flex flex-col justify-between items-center h-full w-full">
          <PieChart data={genresWithColors} size={200} />
          <div className="mt-4 w-full">
            {data.genres.map((item, index) => (
              <div
                key={item.label}
                className="flex justify-between items-center mb-2"
              >
                <div className="flex items-center">
                  <div
                    className="w-3 h-3 rounded-full mr-2"
                    style={{ backgroundColor: colors[index].color }}
                  ></div>
                  <Text>{item.label}</Text>
                </div>
                <Text>
                  {totalBooks > 0
                    ? Math.round(((item.value || 0) / totalBooks) * 100)
                    : 0}
                  % (<LocaleNumber number={item.value || 0} />{" "}
                  {pluralize("book", item.value || 0)})
                </Text>
              </div>
            ))}
          </div>
        </div>
      </div>
    </StatWrapper>
  );
};

export default GenreBreakdownStat;
